import { RelatedToType } from 'app/shared/enums';
import { AccountSubdomain } from '../enums';
import { Dictionary } from 'app/shared/models';
import { TsTableOptions } from 'tsui/lib/ts-table/ts-table.model';
import { User } from '@core/models/user.types';
import { Permissionable } from '../../modules/projects/projects.types';
import { ConditionalStyling } from '@core/models/conditional-styling';

export type DisplayCurrency = 'USD' | 'CAN' | 'EUR' | 'GBP' | string;

export interface Account {
  current_subscription?: any;
  definitions?: Dictionary<any> | any;
  display_currency?: DisplayCurrency;
  features?: string[];
  hidden_standard_columns?: Dictionary<[]>;
  id: string;
  image_url?: string;
  merge_all_comments_within_object?: boolean;
  name: string;
  num_members?: number;
  num_projects?: number;
  errors?: any;
  owner_id?: number;
  run_signup_onboarding?: boolean;
  show_history_standard_columns?: Dictionary<[]>;
  show_images_on_pipeline?: boolean;
  subdomain?: AccountSubdomain | string;
  token?: string;
  url?: string;
  visibility?: string;
  workspace_type?: WorkspaceType;
  colors?: AccountColor[];
  saml_setting?: any;
  sso_required?: boolean;
  celeste_url?: string;
  parceled_team_id?: string;
  tahoe_tenant_id?: string;
  tahoe_tenant_alias?: string;
  tahoe_tenant_url?: string;
}

export interface AccountColor {
  id: number;
  name: string;
  label: string;
  hex_value: string;
  color_type: 'primary' | 'secondary' | 'none';
  _destroy: boolean;
}

export interface AccountSettings {
  id: string;
  name: string;
  subdomain: string;
  display_currency: DisplayCurrency;
  workspace_type?: WorkspaceType;
  configuration: {
    show_images_on_pipeline?: boolean;
    merge_all_comments_within_object?: boolean;
    definitions?: string;
    celeste_url?: string;
  };
  colors: AccountColor[];
  sso_required?: boolean;
  tahoe_tenant_id?: string;
  tahoe_tenant_alias?: string;
  tahoe_tenant_url?: string;
}

// API: account.workspace_type values
export type WorkspaceType = 'client' | 'poc' | 'internal';

export const WORKSPACE_TYPE_OPTIONS: { label: string; value: WorkspaceType }[] = [
  { label: 'Client', value: 'client' },
  { label: 'POC', value: 'poc' },
  { label: 'Internal', value: 'internal' },
];

export interface BaseAccountType {
  id: number;
  name: string;
  hidden: boolean;
}

export interface ProjectStage extends BaseAccountType {
  rank: number;
  active?: boolean;
  collapse?: boolean;
  pipeline_ids?: any;
  color?: string;
  required_fields: any[];
}

export interface ProjectPhase extends BaseAccountType {
  rank: number;
  active?: boolean;
  collapse?: boolean;
  pipeline_ids?: any;
  required_fields: any;
}

export interface PropertyType extends BaseAccountType {
  account_id?: number;
  rank?: number;
  color?: string;
}

export interface ProjectType extends BaseAccountType {
  rank?: number;
}

export interface Tag {
  id?: string;
  title?: string;
}

export interface LoanPurpose extends BaseAccountType {
  rank?: number;
}

export interface InvestmentType extends BaseAccountType {
  rank?: number;
}

export interface Team extends BaseAccountType {
  children?: Team[];
  depth?: number;
  mention_name?: string;
  parent_id?: number;
  path?: string[];
  rank?: number;
  users?: User[];
}

export interface Vehicle extends BaseAccountType {
  rank?: number;
}

export interface CallOutcome extends BaseAccountType {
  rank?: number;
}

export interface MeetingOutcome extends BaseAccountType {
  rank?: number;
}

export interface EngagementActivityType extends BaseAccountType {
  rank?: number;
}

export interface MarketStatus extends BaseAccountType {
  rank?: number;
}

export interface SocialMediaSource extends BaseAccountType {
  rank?: number;
}

export type FieldDefType =
  | 'number'
  | 'money'
  | 'text'
  | 'percent'
  | 'select'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'decimal'
  | 'textarea'
  | 'record'
  | 'url'
  | 'typeahead'
  | 'tags'
  | 'year'
  | 'id';

export interface FieldDef {
  account_id?: number;
  created_at?: Date | string;
  created_by_id?: number;
  created_by_user_name?: string;
  custom_object_id?: number;
  description?: string;
  archived?: boolean;
  id?: number;
  import?: boolean;
  label: string;
  locked?: boolean;
  meta?: FieldDefMeta;
  hidden?: boolean;
  name: string;
  related_to_type?: RelatedToType;
  inherited?: boolean;
  type: FieldDefType;
  updated_at?: Date | string;
  related_to_id?: number;
  displayGroupedType?: string;
  val?: any;
  required_for_import?: boolean;
  usedForTimeSeries?: boolean; // To indicate that the field can hold time series data.
  _parentFieldName?: string; // Field injected in nested fields.
  _parentFieldLabel?: string;
  _parentFieldMeta?: FieldDefMeta;
  task_role_id?: number;
  tsTableOptions?: TsTableOptions;
  conditional_styling?: ConditionalStyling[];
  isRequired?: boolean; // Synthetic attribute to specify if field is required inside the 'Add Form' drawer.
  isRemovable?: boolean; // Synthetic attribute to specify if field is removable inside the 'Add Form' drawer.
  lockedByTaskLink?: boolean; // Synthetic FE-only attribute: field is locked because it is linked to a task key date.
}

export type FieldDefMeta = {
  option_name?: string;
  related_to_type?: any;
  calc_code?: string | null;
  default_calc_code?: string;
  calc_index?: number;
  filtering?: {
    operators?: string[];
    values?: string[];
    functions?: any[];
    exclude_in_nested_filter?: boolean;
  };
  reporting?: {
    hidden?: boolean;
  };
  allowed_record_formats?: string[];
  is_calculated?: boolean;
  is_custom?: boolean;
  multi_select?: boolean;
  options?: any[];
  readonly?: boolean;
  record_type?: string;
  scale?: any;
  thousands_separator?: boolean;
  sorting?: string;
  user_editable?: boolean;
  // the following props do not come from the API, they are FE only
  shapeShifter?: FieldDef;
  shapeShifted?: boolean;
  group?: any;
  groupable?: boolean;
  value_hidden?: boolean;
};

export interface SavedFilter {
  id: number;
  name: string;
  filters?: string;
  account_id?: number;
  visible?: boolean;
}

export interface Activity {
  account_id: number;
  action: string;
  api_vendor_id?: number;
  avatar_url?: string;
  created_at: string;
  dd_trace_id?: number;
  deleted_at?: string;
  field: string;
  field_label: string;
  field_type: string;
  id: number;
  new_val: any;
  old_val: any;
  old_value_duration?: number | string;
  old_value_duration_days?: number | string;
  pipeline_id: number;
  pipeline_name: string;
  project_id: number;
  project_title: string;
  related_to_id: number;
  related_to_label: string;
  related_to_name: string;
  related_to_type: RelatedToType;
  secondary_related_to_id?: any;
  secondary_related_to_label?: any;
  secondary_related_to_name?: any;
  secondary_related_to_type?: any;
  source?: string;
  summary: string;
  user_id: number;
  user_name: string;
}

export interface ActivityResponse {
  total: number;
  activities: Activity[];
}

export interface PipelineTemplate {
  id: number;
  name: string;
  body: any;
  category: string;
  sub_category: string;
}

export interface Integration {
  id: number;
  name: string;
  description: string;
  slug: string;
  extra: string;
  new_connections_enabled: boolean;
}

export interface AccountIntegration {
  id: number;
  integration_id: number;
  active: boolean;
  extra: any;
  user_id?: string | number | null;
  integration: Integration;
  details: any;
  account_id: number;
  token: string;
  jsonExtra: any;
}

export interface SavedView extends Partial<Permissionable> {
  id: number;
  name: string;
  definition?: any;
  filters?: string;
  related_to_type?: string;
  parent_object_type?: string | null;
  visiblity?: string;
  account_id?: number;
  saved_filter?: SavedFilter;
  created_at?: any;
  rank?: number | null;
}
